import { Box, TextField, Paper, Stack, Divider, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import TablaInformes from "@/components/Tablas/TablaInformes/TablaInformes";
import { useEffect, useState } from "react";
import { informeApi } from "@/redux/api/informeApi";
import { IInforme } from "@/redux/models/typesInformes";
import TopWrapper from "../DashboardLayout/TopWrapper";

const PapeleraPage = () => {
    const [obtenerInformes] = informeApi.endpoints.obtenerInformesReciclados.useLazyQuery()
    const [informesLoading, setInformesLoading] = useState<boolean>(true)
    const [informes, setInformes] = useState<IInforme[]>([])

    // Estados de paginación
    const [rowsPerPage] = useState<number>(20) // registros por página
    const [totalInformes, setTotalInformes] = useState<number>(0) // total de registros

    // Estados de búsqueda
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [isSearching, setIsSearching] = useState<boolean>(false)
    const [allInformes, setAllInformes] = useState<IInforme[]>([]) // Para mantener todos los informes sin filtrar

    // Función para cargar informes con paginación
    const cargarInformes = async (pageNumber: number, filteredData?: IInforme[]) => {
        setInformesLoading(true)
        try {
            // Si tenemos datos filtrados, usar esos en lugar de hacer una nueva consulta
            if (filteredData) {
                const startIndex = pageNumber * rowsPerPage
                const endIndex = startIndex + rowsPerPage
                const informesPaginados = filteredData.slice(startIndex, endIndex)
                setInformes(informesPaginados)
                setTotalInformes(filteredData.length)
                return
            }

            const desde = (pageNumber * rowsPerPage) + 1
            const hasta = desde + rowsPerPage - 1

            const informesData = await obtenerInformes({ desde, hasta }).unwrap()
            setInformes(informesData)

            // Si es la primera página, obtener el total para la paginación
            if (pageNumber === 0 && informesData.length > 0) {
                // Hacer una consulta para obtener todos los registros y guardarlos
                const totalData = await obtenerInformes({ desde: 1, hasta: 99999 }).unwrap()
                setAllInformes(totalData)
                setTotalInformes(totalData.length)
            } else if (pageNumber === 0) {
                setTotalInformes(0)
                setAllInformes([])
            }
        } catch (error) {
            console.error('Error al cargar informes:', error)
            setInformes([])
            setTotalInformes(0)
            setAllInformes([])
        } finally {
            setInformesLoading(false)
        }
    }

    useEffect(() => {
        cargarInformes(0) // Cargar primera página al inicializar
    }, [obtenerInformes])


    // Función para manejar cambio de página
    const handlePageChange = async (newPage: number): Promise<void> => {
        if (searchTerm.trim()) {
            // Si estamos en modo búsqueda, paginar los resultados filtrados
            const filteredInformes = filterInformes(allInformes, searchTerm)
            await cargarInformes(newPage, filteredInformes)
        } else {
            // Paginación normal
            await cargarInformes(newPage)
        }
    }

    // Función para filtrar informes localmente
    const filterInformes = (informesToFilter: IInforme[], term: string): IInforme[] => {
        if (!term.trim()) return informesToFilter

        const lowerSearchTerm = term.toLowerCase()

        return informesToFilter.filter(informe => {
            const informeString = JSON.stringify(informe).toLowerCase()
            return informeString.includes(lowerSearchTerm)
        })
    }

    // Función para manejar la búsqueda
    const handleSearch = async () => {
        if (!searchTerm.trim()) {
            // Si no hay término de búsqueda, recargar todos los informes
            await cargarInformes(0)
            return
        }

        setIsSearching(true)
        try {
            // Asegurar que tenemos todos los informes cargados
            if (allInformes.length === 0) {
                const todosLosInformes = await obtenerInformes({ desde: 1, hasta: 99999 }).unwrap()
                setAllInformes(todosLosInformes)

                // Filtrar y mostrar resultados
                const filteredInformes = filterInformes(todosLosInformes, searchTerm)
                await cargarInformes(0, filteredInformes)
            } else {
                // Usar los informes ya cargados
                const filteredInformes = filterInformes(allInformes, searchTerm)
                await cargarInformes(0, filteredInformes)
            }
        } catch (error) {
            console.error('Error en la búsqueda:', error)
        } finally {
            setIsSearching(false)
        }
    }

    // Función para limpiar la búsqueda
    const handleClearSearch = async () => {
        setSearchTerm('')
        await cargarInformes(0)
    }

    const handleRestaurarInforme = (informe: IInforme) => {
        const nuevos_informes = informes.filter((i) => i.id !== informe.id)
        setInformes(nuevos_informes)

        // Actualizar también allInformes para mantener consistencia
        const nuevos_all_informes = allInformes.filter((i) => i.id !== informe.id)
        setAllInformes(nuevos_all_informes)

        // Actualizar el total después de restaurar
        setTotalInformes(prev => prev - 1)
    }

    // Manejar Enter en el campo de búsqueda
    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSearch()
        }
    }


    return (<>
        <TopWrapper
            title='Papelera'
            subtitle='Mirá tus informes borrados desde esta sección'
        />


        <Box sx={{
            maxWidth: '1200px',
            margin: '0 auto',
        }}
        >
            {/* Componente de búsqueda */}
            <Paper elevation={2} sx={{ mb: 3, p: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                    Buscar en Papelera
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                    <TextField
                        fullWidth
                        size="small"
                        label="Buscar informes..."
                        placeholder="Buscar por entrada, tipo, observaciones, usuario, ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleKeyDown}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
                        }}
                        sx={{ maxWidth: { sm: '400px' } }}
                    />
                    <Stack direction="row" spacing={1}>
                        <LoadingButton
                            variant="contained"
                            size="small"
                            onClick={handleSearch}
                            loading={isSearching}
                            startIcon={<SearchIcon />}
                            sx={{ minWidth: '100px' }}
                        >
                            Buscar
                        </LoadingButton>
                        {searchTerm && (
                            <LoadingButton
                                variant="outlined"
                                size="small"
                                onClick={handleClearSearch}
                                startIcon={<ClearIcon />}
                                sx={{ minWidth: '100px' }}
                            >
                                Limpiar
                            </LoadingButton>
                        )}
                    </Stack>
                </Stack>
                {searchTerm && (
                    <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                        {totalInformes > 0
                            ? `Se encontraron ${totalInformes} resultado(s) para "${searchTerm}"`
                            : `No se encontraron resultados para "${searchTerm}"`
                        }
                    </Typography>
                )}
            </Paper>

            <TablaInformes
                hideColumns={['ACTION_LIQUIDAR', 'ACTION_EDITAR']}
                informes={informes}
                isLoading={informesLoading}
                onPageChange={handlePageChange}
                totalInformes={totalInformes}
                rowsPerPage={rowsPerPage}
                onInformeRestaurado={handleRestaurarInforme}
            />

        </Box>

    </>);
}

export default PapeleraPage;